import { useCallback, useLayoutEffect, useState } from 'react';
import type { Size } from './markupGeometry';

const EMPTY: Size = { width: 0, height: 0 };

export function useStageSize<T extends HTMLElement>(): { ref: (el: T | null) => void; size: Size } {
    const [element, setElement] = useState<T | null>(null);
    const [size, setSize] = useState<Size>(EMPTY);
    const ref = useCallback((el: T | null) => setElement(el), []);

    useLayoutEffect(() => {
        if (!element) return undefined;
        const measure = (width: number, height: number) =>
            setSize((prev) =>
                prev.width === width && prev.height === height ? prev : { width, height }
            );
        const rect = element.getBoundingClientRect();
        measure(rect.width, rect.height);
        const observer = new ResizeObserver((entries) => {
            const box = entries[0]?.contentRect;
            if (box) measure(box.width, box.height);
        });
        observer.observe(element);
        return () => observer.disconnect();
    }, [element]);

    return { ref, size };
}
